import { endOfDay, format, startOfDay } from 'date-fns';
import { CalendarDays } from 'lucide-react';
import { useState, type ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar } from '@avanta/shared/components/ui/Calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@avanta/shared/components/ui/Popover';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { type TableDateRange } from '@avanta/shared/types/table';
import { cn } from '@avanta/shared/utils/cn';

type CalendarConstraints = Pick<
  ComponentProps<typeof Calendar>,
  'startMonth' | 'endMonth' | 'disabled' | 'captionLayout' | 'showOutsideDays'
>;

interface TableFilterDateRangeProps {
  value?: TableDateRange;
  onChange: (value: TableDateRange | undefined) => void;
  disabled?: boolean;
  className?: string;
  calendarProps?: CalendarConstraints;
}

const dateRangeTriggerClassName =
  'flex h-8 w-full min-w-0 items-center gap-3 rounded-none border-0 bg-white px-4 text-left text-xs font-normal text-denim-900 shadow-none hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-teal-500';

export const TableFilterDateRange = ({
  value,
  onChange,
  disabled = false,
  className,
  calendarProps,
}: TableFilterDateRangeProps): React.ReactElement => {
  const { i18n, t } = useTranslation(LOCIZE_NS.COMMON);
  const [openField, setOpenField] = useState<'from' | 'to'>();

  const fromLabel = t('Common.dateRange.from', { defaultValue: 'Desde:' });
  const toLabel = t('Common.dateRange.to', { defaultValue: 'Hasta' });
  const appLanguage = i18n.resolvedLanguage ?? i18n.language;

  const updateRange = (key: 'from' | 'to', date: Date | undefined): void => {
    const nextRange: TableDateRange = { ...value, [key]: date };

    if (!nextRange.from && !nextRange.to) {
      onChange(undefined);
      return;
    }

    onChange(nextRange);
  };

  const mergeDisabled = (constraint: (date: Date) => boolean): CalendarConstraints['disabled'] => {
    const existing = calendarProps?.disabled;

    if (!existing) return constraint;
    return Array.isArray(existing) ? [...existing, constraint] : [existing, constraint];
  };

  const fromDisabled = (date: Date): boolean => Boolean(value?.to && date > endOfDay(value.to));
  const toDisabled = (date: Date): boolean => Boolean(value?.from && date < startOfDay(value.from));

  return (
    <div
      className={cn(
        'flex h-8 w-full min-w-0 overflow-hidden rounded-lg border border-plum-200 bg-white shadow-box-xs am-lg:max-w-81.5',
        className
      )}
    >
      <Popover
        open={openField === 'from'}
        onOpenChange={open => setOpenField(open ? 'from' : undefined)}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            data-slot="input"
            disabled={disabled}
            aria-label={fromLabel}
            className={dateRangeTriggerClassName}
          >
            <CalendarDays className="size-4 shrink-0 text-teal-600" strokeWidth={1.5} />
            <span className="truncate">
              <span>{fromLabel} </span>
              <span className="font-bold">
                {value?.from ? format(value.from, 'dd/MM/yy') : '00/00/00'}
              </span>
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            {...calendarProps}
            appLanguage={appLanguage}
            selected={value?.from}
            onSelect={(date: Date | undefined) => {
              updateRange('from', date);
              if (date) setOpenField(undefined);
            }}
            defaultMonth={value?.from ?? value?.to}
            disabled={mergeDisabled(fromDisabled)}
          />
        </PopoverContent>
      </Popover>
      <Popover
        open={openField === 'to'}
        onOpenChange={open => setOpenField(open ? 'to' : undefined)}
      >
        <PopoverTrigger asChild>
          <button
            type="button"
            data-slot="input"
            disabled={disabled}
            aria-label={toLabel}
            className={cn(dateRangeTriggerClassName, 'border-l border-gray-100')}
          >
            <CalendarDays className="size-4 shrink-0 text-teal-600" strokeWidth={1.5} />
            <span className="truncate">
              <span>{toLabel} </span>
              <span className="font-bold">
                {value?.to ? format(value.to, 'dd/MM/yy') : '00/00/00'}
              </span>
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-auto p-0">
          <Calendar
            {...calendarProps}
            appLanguage={appLanguage}
            selected={value?.to}
            onSelect={(date: Date | undefined) => {
              updateRange('to', date);
              if (date) setOpenField(undefined);
            }}
            defaultMonth={value?.to ?? value?.from}
            disabled={mergeDisabled(toDisabled)}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
};

export type { TableFilterDateRangeProps };
